/**
 * Create Swarm Form
 * Form for creating new agent swarms
 */

import React, { useState } from 'react';
import { SwarmClient } from '@mindcore/sdk';
import { Loader2, Sparkles } from 'lucide-react';

interface CreateSwarmFormProps {
  client: SwarmClient;
  onCreated: (swarmId: string) => void;
  onCancel: () => void;
}

const PROJECT_TYPES = [
  { value: 'app', label: 'Web Application', description: 'Full-stack web app with frontend and backend' },
  { value: 'api', label: 'API Service', description: 'REST or GraphQL API with documentation' },
  { value: 'infrastructure', label: 'Infrastructure', description: 'Terraform/CloudFormation templates' },
  { value: 'component', label: 'UI Component', description: 'Reusable React/Vue component' },
];

const COMPLIANCE_OPTIONS = [
  { value: 'soc2', label: 'SOC 2', description: 'Security and availability controls' },
  { value: 'hipaa', label: 'HIPAA', description: 'Healthcare data protection' },
  { value: 'pci', label: 'PCI DSS', description: 'Payment card industry standards' },
  { value: 'gdpr', label: 'GDPR', description: 'EU data privacy regulations' },
];

const CLOUD_PROVIDERS = [
  { value: 'cloudflare', label: 'Cloudflare', description: 'Workers, Pages, D1, R2' },
  { value: 'aws', label: 'AWS', description: 'Lambda, ECS, RDS, S3' },
  { value: 'gcp', label: 'Google Cloud', description: 'Cloud Run, Firestore, Cloud Storage' },
  { value: 'azure', label: 'Azure', description: 'Container Apps, Cosmos DB, Blob Storage' },
];

export const CreateSwarmForm: React.FC<CreateSwarmFormProps> = ({
  client,
  onCreated,
  onCancel,
}) => {
  const [prompt, setPrompt] = useState('');
  const [projectType, setProjectType] = useState('app');
  const [selectedCompliance, setSelectedCompliance] = useState<string[]>([]);
  const [selectedClouds, setSelectedClouds] = useState<string[]>(['cloudflare']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const session = await client.build(prompt, {
        projectType: projectType as any,
        compliance: selectedCompliance as ('soc2' | 'hipaa' | 'pci' | 'gdpr')[],
        targetClouds: selectedClouds,
        maxAgents: 7,
      });

      onCreated(session.getSwarmId());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create swarm');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleCompliance = (value: string) => {
    setSelectedCompliance(prev =>
      prev.includes(value)
        ? prev.filter(c => c !== value)
        : [...prev, value]
    );
  };

  const toggleCloud = (value: string) => {
    setSelectedClouds(prev =>
      prev.includes(value)
        ? prev.filter(c => c !== value)
        : [...prev, value]
    );
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Create New Swarm</h2>
            <p className="text-gray-600">Describe what you want to build and our AI agents will create it.</p>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700"
          >
            Cancel
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Prompt Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What do you want to build?
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="E.g., Build a HIPAA-compliant patient portal with React frontend, Node.js backend, PostgreSQL database, and deploy to AWS..."
              className="w-full h-32 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              required
              minLength={10}
            />
            <p className="mt-1 text-sm text-gray-500">
              Be specific about features, tech stack, and compliance requirements.
            </p>
          </div>

          {/* Project Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              {PROJECT_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setProjectType(type.value)}
                  className={`p-4 border rounded-lg text-left transition-colors ${
                    projectType === type.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-gray-900">{type.label}</div>
                  <div className="text-sm text-gray-500">{type.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Cloud Providers */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Cloud Providers
            </label>
            <div className="grid grid-cols-2 gap-3">
              {CLOUD_PROVIDERS.map((cloud) => (
                <button
                  key={cloud.value}
                  type="button"
                  onClick={() => toggleCloud(cloud.value)}
                  className={`p-4 border rounded-lg text-left transition-colors ${
                    selectedClouds.includes(cloud.value)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-gray-900">{cloud.label}</div>
                  <div className="text-sm text-gray-500">{cloud.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Compliance */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Compliance Requirements (Optional)
            </label>
            <div className="grid grid-cols-2 gap-3">
              {COMPLIANCE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleCompliance(option.value)}
                  className={`p-4 border rounded-lg text-left transition-colors ${
                    selectedCompliance.includes(option.value)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-gray-900">{option.label}</div>
                  <div className="text-sm text-gray-500">{option.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 text-gray-700 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !prompt.trim()}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Creating Swarm...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  <span>Create Swarm</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
